import type { CommonData } from '@/components/blockEditor/blockEditor.types';
import { CURRENT_SCHEMA_VERSION } from '@/components/blockEditor/blockEditor.types';

export function getDefaultPhilanthropicPlanningData(): CommonData {
  return {
    version: CURRENT_SCHEMA_VERSION,
    blocks: [
      {
        id: 'description',
        type: 'ListItemsSection',
        props: {},
        children: {
          default: [
            {
              id: 'philanthropic-planning-overview-heading',
              type: 'EducationalSlideHeading',
              props: {
                title: 'Benefits of philanthropic giving',
                variant: 'h3',
              },
            },
            {
              id: 'philanthropic-planning-overview-body',
              type: 'EditableEducationalSlideBody',
              props: {
                text: 'Philanthropy can be a powerful way to advance causes you care about, create a family legacy, and achieve tax benefits. The right charitable giving structure depends on your goals, level of involvement, and desired impact.',
              },
            },
          ],
        },
      },
      {
        id: 'vehicles',
        type: 'ListItemsSection',
        props: {},
        children: {
          default: [
            {
              id: 'philanthropic-planning-columns',
              type: 'TwoColumnLayout',
              props: {
                leftWidth: '50%',
                rightWidth: '50%',
                columnGap: 2,
                showDivider: true,
              },
              children: {
                leftColumn: [
                  {
                    id: 'philanthropic-planning-daf-heading',
                    type: 'EducationalSlideHeading',
                    props: {
                      title: 'Donor-Advised Funds (DAFs)',
                      variant: 'h2',
                    },
                  },
                  {
                    id: 'philanthropic-planning-daf-body',
                    type: 'EditableEducationalSlideBody',
                    props: {
                      text: 'A donor-advised fund is a charitable giving vehicle that allows donors to contribute assets, receive an immediate tax deduction (subject to AGI limits), and recommend grants over time, while the sponsoring charity manages investments, administration, and compliance.',
                    },
                  },
                  {
                    id: 'philanthropic-planning-daf-advantages-heading',
                    type: 'EducationalSlideHeading',
                    props: {
                      title: 'Advantages',
                      variant: 'h3',
                    },
                  },
                  {
                    id: 'philanthropic-planning-daf-advantage-family',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Family involvement',
                      description:
                        'Enables long-term family involvement in charitable decision-making',
                      iconType: 'check',
                      iconColor: 'PRIMARY',
                    },
                  },
                  {
                    id: 'philanthropic-planning-daf-advantage-growth',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Growth potential',
                      description:
                        'Contributed assets can be invested for potential growth before granting',
                      iconType: 'check',
                      iconColor: 'PRIMARY',
                    },
                  },
                  {
                    id: 'philanthropic-planning-daf-advantage-privacy',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Privacy',
                      description: 'Grants can be made anonymously',
                      iconType: 'check',
                      iconColor: 'PRIMARY',
                    },
                  },
                  {
                    id: 'philanthropic-planning-daf-considerations-heading',
                    type: 'EducationalSlideHeading',
                    props: {
                      title: 'Considerations',
                      variant: 'h3',
                    },
                  },
                  {
                    id: 'philanthropic-planning-daf-consideration-advisory',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Limited control',
                      description:
                        'The donor has advisory privileges, but final grant decisions rest with the sponsoring organization',
                      iconType: 'flag',
                      iconColor: 'NEGATIVE',
                    },
                  },
                  {
                    id: 'philanthropic-planning-daf-consideration-irrevocable',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Irrevocability',
                      description:
                        'Once assets are gifted to the DAF account, they cannot be reclaimed by the donor',
                      iconType: 'flag',
                      iconColor: 'NEGATIVE',
                    },
                  },
                ],
                rightColumn: [
                  {
                    id: 'philanthropic-planning-pf-heading',
                    type: 'EducationalSlideHeading',
                    props: {
                      title: 'Private Foundations',
                      variant: 'h2',
                    },
                  },
                  {
                    id: 'philanthropic-planning-pf-body',
                    type: 'EditableEducationalSlideBody',
                    props: {
                      text: 'A Private Foundation is an independent charitable entity controlled by an individual, family, or business that allows for flexible grant making and long-term philanthropic planning, but requires annual charitable distributions, generally equal to at least 5% of investment assets.',
                    },
                  },
                  {
                    id: 'philanthropic-planning-pf-advantages-heading',
                    type: 'EducationalSlideHeading',
                    props: {
                      title: 'Advantages',
                      variant: 'h3',
                    },
                  },
                  {
                    id: 'philanthropic-planning-pf-advantage-family',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Family involvement',
                      description:
                        'Enables long-term family involvement in governance',
                      iconType: 'check',
                      iconColor: 'PRIMARY',
                    },
                  },
                  {
                    id: 'philanthropic-planning-pf-advantage-control',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Control over giving',
                      description:
                        'Significant control over grants and charitable activities, subject to IRS rules',
                      iconType: 'check',
                      iconColor: 'PRIMARY',
                    },
                  },
                  {
                    id: 'philanthropic-planning-pf-advantage-staff',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Direct operations',
                      description: 'Can hire staff and run programs directly',
                      iconType: 'check',
                      iconColor: 'PRIMARY',
                    },
                  },
                  {
                    id: 'philanthropic-planning-pf-considerations-heading',
                    type: 'EducationalSlideHeading',
                    props: {
                      title: 'Considerations',
                      variant: 'h3',
                    },
                  },
                  {
                    id: 'philanthropic-planning-pf-consideration-costs',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Higher costs',
                      description:
                        'Higher setup and ongoing administrative costs, including an excise tax on net investment income',
                      iconType: 'flag',
                      iconColor: 'NEGATIVE',
                    },
                  },
                  {
                    id: 'philanthropic-planning-pf-consideration-payout',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Regulatory complexity',
                      description:
                        'Must meet annual payout requirements and comply with strict IRS rules, including limits on self-dealing and additional reporting obligations',
                      iconType: 'flag',
                      iconColor: 'NEGATIVE',
                    },
                  },
                ],
              },
            },
          ],
        },
      },
    ],
  };
}
